import React from "react";
import { Reveal, MaskText } from "./Reveal";
import { ContactForm } from "./Forms";
import { motion } from "framer-motion";

const FINALE = [
  "Opening Ceremony", "Heritage Khadi Showcase", "Designer Collections", "Celebrity Showstopper",
  "National Awards", "Artisan Honour Ceremony", "Fashion Innovation Awards", "Live Performances",
  "Cultural Presentations", "Gala Dinner", "Networking Summit",
];

export const GrandFinale = () => (
  <section id="finale" data-testid="finale-section" className="relative khadi-tex-dark bg-[#1a130b] text-[#f2ece0] py-28 md:py-40 px-5 md:px-10 grain overflow-hidden">
    <div className="mx-auto max-w-[1600px]">
      <Reveal>
        <p className="font-display text-[11px] tracking-[0.3em] uppercase text-[#a65a34] mb-6">(10) The Grand Finale</p>
      </Reveal>
      <h2 className="font-serif text-5xl md:text-8xl leading-[0.92] tracking-tight max-w-5xl">
        <Reveal><span>A world-class</span></Reveal>
        <Reveal delay={0.1}><span className="italic text-[#a65a34]">national spectacle.</span></Reveal>
      </h2>

      <div className="mt-16 columns-1 md:columns-2 lg:columns-3 gap-12">
        {FINALE.map((f, i) => (
          <Reveal key={f} delay={(i % 4) * 0.05}>
            <div className="group mb-3 flex items-baseline gap-4 border-b border-[#f2ece0]/12 pb-3 break-inside-avoid transition-colors hover:border-[#a65a34]">
              <span className="font-display text-[10px] text-[#f2ece0]/30">{String(i + 1).padStart(2, "0")}</span>
              <span className="font-serif text-2xl md:text-3xl tracking-tight">{f}</span>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

export const ClosingCTA = () => (
  <section data-testid="closing-section" className="relative khadi-tex-dark bg-[#a65a34] text-[#f2ece0] py-32 md:py-52 px-5 md:px-10 grain">
    <div className="mx-auto max-w-[1500px] text-center">
      <Reveal>
        <p className="font-display text-[11px] tracking-[0.4em] uppercase text-[#f2ece0]/70 mb-10">The Closing Word</p>
      </Reveal>
      <h2 className="font-serif uppercase leading-[0.92] tracking-tight text-[9vw] md:text-[6.5vw]">
        <MaskText start lines={["India's Heritage", "Deserves a", "Global Runway"]} lineClassName="" />
      </h2>
      <Reveal delay={0.3}>
        <p className="mx-auto mt-12 max-w-2xl font-body text-base md:text-lg text-[#f2ece0]/85 leading-relaxed">
          Khadi Fashion Icon India is more than a fashion event — it is a national movement to
          inspire creativity, empower artisans, nurture young talent and redefine India&apos;s textile
          legacy for the world. Together, let us shape the future of sustainable Indian fashion.
        </p>
      </Reveal>
      <Reveal delay={0.4}>
        <p className="mt-14 font-serif italic text-3xl md:text-5xl">
          Wear India&apos;s Heritage. Walk India&apos;s Future.
        </p>
      </Reveal>
    </div>
  </section>
);

export const Contact = () => (
  <section id="contact" data-testid="contact-section" className="relative khadi-tex bg-[#f2ece0] py-28 md:py-40 px-5 md:px-10">
    <div className="mx-auto max-w-[1600px] grid md:grid-cols-12 gap-16">
      <div className="md:col-span-5">
        <Reveal>
          <p className="font-display text-[11px] tracking-[0.3em] uppercase text-[#a65a34] mb-6">Get in Touch</p>
          <h2 className="font-serif text-5xl md:text-7xl leading-[0.92] tracking-tight">
            Join the movement.
          </h2>
          <p className="mt-8 font-body text-black/60 leading-relaxed max-w-md">
            Designers, artisans, institutions, sponsors and media — reach out and become part of
            India&apos;s largest Khadi fashion platform.
          </p>
          <div className="mt-10 space-y-2 font-body text-sm text-black/70">
            <p>hello@khadifashionicon.in</p>
            <p>+91 98765 43210</p>
            <p>New Delhi · Pan-India</p>
          </div>
        </Reveal>
      </div>
      <div className="md:col-span-7 md:pl-10 md:border-l border-black/10">
        <Reveal delay={0.1}>
          <ContactForm />
        </Reveal>
      </div>
    </div>
  </section>
);

export const Footer = () => (
  <footer data-testid="site-footer" className="relative khadi-tex-dark bg-[#1a130b] text-[#f2ece0] px-5 md:px-10 pt-20 pb-10 grain">
    <div className="mx-auto max-w-[1600px]">
      <div className="overflow-hidden">
        <motion.h2
          initial={{ y: "110%" }}
          whileInView={{ y: "0%" }}
          viewport={{ once: true }}
          transition={{ duration: 1, ease: [0.76, 0, 0.24, 1] }}
          className="font-serif text-[16vw] md:text-[13vw] leading-[0.82] tracking-tighter"
        >
          Khadi<span className="text-[#a65a34]">.</span>
        </motion.h2>
      </div>
      <div className="mt-12 flex flex-col md:flex-row items-start md:items-end justify-between gap-8 border-t border-[#f2ece0]/15 pt-8">
        <div className="font-display text-[10px] tracking-[0.3em] uppercase text-[#f2ece0]/50 max-w-xs leading-relaxed">
          Fashion Icon India — A National Movement celebrating Heritage, Sustainability &amp; Fashion Innovation.
        </div>
        <div className="flex gap-8 font-display text-[10px] tracking-[0.25em] uppercase text-[#f2ece0]/60">
          <span className="hover:text-[#a65a34] transition-colors cursor-pointer">Instagram</span>
          <span className="hover:text-[#a65a34] transition-colors cursor-pointer">YouTube</span>
          <span className="hover:text-[#a65a34] transition-colors cursor-pointer">LinkedIn</span>
        </div>
      </div>
      <p className="mt-10 font-body text-[11px] text-[#f2ece0]/30">
        © {new Date().getFullYear()} Khadi Fashion Icon India. All rights reserved.
      </p>
    </div>
  </footer>
);

export default GrandFinale;
